import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart } from 'lucide-react';
import { SEO } from '../components/SEO';

const values = [
  {
    icon: Target,
    title: 'Gyakorlatiasság',
    desc: 'Nálunk nincs öncélú elmélet. Minden, amit tanítunk, azonnal alkalmazható a mindennapokban.',
  },
  {
    icon: Eye,
    title: 'Őszinteség',
    desc: 'Nem ígérünk csodákat. A fejlődés munka, és mi megmutatjuk, hogyan végezd hatékonyan.',
  },
  {
    icon: Heart,
    title: 'Emberközpontúság',
    desc: 'A készségek mögött emberek vannak. Tiszteljük a résztvevők idejét, tapasztalatát és céljait.',
  },
];

const milestones = [
  { year: '2003', event: 'A képzési szemlélet és szakmai háttér megszületése.' },
  { year: '2008', event: 'Az első vállalati tréningprogramok elindítása.' },
  { year: '2012', event: 'Az ingatlanpiaci szakmai képzések bevezetése.' },
  { year: '2018', event: 'Az Akadémia Plusz 2.0 Kft. megalapítása.' },
  { year: '2023', event: 'Több mint 5000 képzett szakember az Akadémia programjain.' },
];

export const AboutPage = () => {
  return (
    <main data-testid="about-page">
      <SEO
        title="Rólunk"
        description="Az Akadémia Plusz 2.0 Kft. története, értékeink és küldetésünk. Több mint 20 év tapasztalat a gyakorlati készségfejlesztésben."
        path="/rolunk"
      />
      {/* Hero */}
      <section className="relative min-h-[40vh] md:min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img loading="lazy"
            src="https://images.unsplash.com/photo-1721623777765-1381ba32859c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBwcm9mZXNzaW9uYWwlMjBzcGVha2VyJTIwcHJlc2VudGF0aW9uJTIwZGFyayUyMHN0YWdlfGVufDB8fHx8MTc3MjExMzA5N3ww&ixlib=rb-4.1.0&q=85"
            alt="Készségfejlesztő tréner előadás közben — Az Akadémia"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#404041]/70 via-[#404041]/50 to-[#404041]" />
        </div>

        <div className="container-custom relative z-10 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-brand-light-green font-mono text-sm tracking-widest uppercase mb-4">
              Rólunk
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontKerning: 'normal', letterSpacing: '0.03em' }}>
              Több mint 20 év készségfejlesztésben
            </h1>
            <p className="text-xl text-white/70">
              Olyan készségeket adunk, amelyek működnek. Nem jegyzetekben, hanem helyzetekben.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-[#404041]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                A történetünk
              </h2>
              <div className="space-y-4 text-white/70 text-lg">
                <p>
                  Az Akadémiát 2003-ban alapítottuk, egyetlen meggyőződéssel: a valódi tudás 
                  nem előadásokban él, hanem helyzetekben bizonyít.
                </p>
                <p>
                  Az elmúlt húsz évben több ezer embert képeztünk ki — pályakezdőktől a 
                  tapasztalt vezetőkig. Közben mi is tanultunk: minden résztvevő, minden 
                  tréning új perspektívát hozott.
                </p>
                <p>
                  Ma az Akadémia Magyarország egyik vezető készségfejlesztő központja. 
                  De a méret sosem volt cél — a minőség és a hatékonyság az, ami hajt minket.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              className="space-y-4"
            >
              {milestones.map((milestone, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-6 p-4 glass-card hover:border-brand-light-blue/30 transition-colors"
                >
                  <span className="font-heading text-2xl font-bold text-brand-light-green">
                    {milestone.year}
                  </span>
                  <span className="text-white">{milestone.event}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#05435a]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Értékeink
            </h2>
            <p className="text-white/60">
              Nem üres szavak, hanem elvek, amelyek minden döntésünket irányítják.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.3 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#07516B]/20 flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <value.icon size={32} className="text-brand-light-green" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-white/60">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-padding bg-[#404041]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false, amount: 0.3 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                A mi szemléletünk
              </h2>
              <div className="space-y-4 text-white/70 text-lg">
                <p>
                  Nem hiszünk az egyszeri kurzusokban, amelyek után visszatérsz a régi 
                  rutinhoz. A mi programjaink úgy épülnek fel, hogy az új készségek 
                  beépüljenek a mindennapjaidba.
                </p>
                <p>
                  Minden tréningünk interaktív. Nem előadásokat tartunk, hanem helyzeteket 
                  teremtünk. Kipróbálod, hibázol, tanulsz — biztonságos környezetben, 
                  mielőtt élesben kellene alkalmaznod.
                </p>
                <p>
                  És ami a legfontosabb: megértjük, hogy az üzleti készségek nem válnak 
                  el a személyes fejlődéstől. Aki jobban kommunikál a munkahelyén, 
                  jobban kommunikál otthon is.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: false, amount: 0.3 }}
              className="glass-card p-6 md:p-10 brand-glow"
            >
              <blockquote className="text-lg md:text-2xl font-heading text-white mb-6">
                „Mondd el, és elfelejtem. Mutasd meg, és emlékezni fogok. Engedd, hogy csináljam, és megértem."
              </blockquote>
              <p className="text-brand-light-green">— Konfuciusz</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#05435a]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Ismerjük meg egymást
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-8">
              Ha felkeltettük az érdeklődésedet, foglalj egy ingyenes konzultációt. 
              Beszéljük meg, miben tudunk segíteni.
            </p>
            <Link
              to="/#kapcsolat"
              className="btn-primary inline-flex items-center gap-2 group"
              data-testid="about-cta-booking"
            >
              Lépj velünk kapcsolatba
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
